import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import moment from "moment";
import adminSurveyService from "../../services/adminSurveyService";
import EmptyState from "../EmptyState";
import AdminAIChatList from "./AdminAIChatList";

// Arc path helpers for pie chart
function polarToCartesian(cx, cy, r, angleInDegrees) {
  const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
  return {
    x: cx + (r * Math.cos(angleInRadians)),
    y: cy + (r * Math.sin(angleInRadians))
  };
}

function describeArc(cx, cy, r, startAngle, endAngle) {
  // Handle full circle case (360 degrees)
  if (Math.abs(endAngle - startAngle) >= 359.9) {
    return [
      "M", cx - r, cy,
      "A", r, r, 0, 1, 0, cx + r, cy,
      "A", r, r, 0, 1, 0, cx - r, cy
    ].join(" ");
  }
  
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
  return [
    "M", start.x, start.y,
    "A", r, r, 0, largeArcFlag, 0, end.x, end.y
  ].join(" ");
}

// Color arrays for charts
const BarColors = [
  'bg-blue-500',
  'bg-orange-300',
  'bg-yellow-300',
  'bg-blue-200',
  'bg-gray-200',
  'bg-green-500',
  'bg-purple-500',
  'bg-red-500'
];

const GraphColors = [
  'rgb(59, 130, 246)',
  'rgb(251, 146, 60)',
  'rgb(252, 211, 77)',
  'rgb(147, 197, 253)',
  'rgb(156, 163, 175)',
  'rgb(34, 197, 94)',
  'rgb(168, 85, 247)',
  'rgb(239, 68, 68)'
];

const HoverColors = [
  'text-blue-600',
  'text-orange-600',
  'text-yellow-600',
  'text-blue-500',
  'text-gray-600',
  'text-green-600',
  'text-purple-600',
  'text-red-600'
];

export default function AdminSurveyAnalytics() {
  const { id } = useParams();
  const [surveyData, setSurveyData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [surveyName, setSurveyName] = useState("");
  const [otherResponses, setOtherResponses] = useState([]);
  const [otherPercentage, setOtherPercentage] = useState(0);
  const [answersWithTimestamp, setAnswersWithTimestamps] = useState([]);
  const [totalAnswers, setTotalAnswers] = useState(0);
  const [tooltip, setTooltip] = useState(null);
  const [insights, setInsights] = useState([]);
  const [isGeneratingInsights, setIsGeneratingInsights] = useState(false);

  useEffect(() => {
    if (id) {
      fetchAnalytics();
    }
  }, [id]);

  const fetchAnalytics = async () => {
    try {
      setIsLoading(true);
      const response = await adminSurveyService.getAdminSurveyAnalytics(id);
      const { survey, analytics, otherResponses, otherResponsesPercentage, answersWithTimestamps, totalAnswers } = response;
      
      setSurveyData(survey);
      setSurveyName(survey.name);
      setAnalyticsData(analytics);
      setTotalAnswers(totalAnswers);
      setOtherResponses(otherResponses);
      setAnswersWithTimestamps(answersWithTimestamps);
      setOtherPercentage(otherResponsesPercentage);
      
      // Set insights if they exist
      if (survey.insights && survey.insights.length > 0) {
        setInsights(survey.insights);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const isMultipleChoice = surveyData?.type === 'multiple-choice';

  const generateInsights = async () => {
    try {
      setIsGeneratingInsights(true);
      const response = await adminSurveyService.generateAdminSurveyInsights(id);
      if (response.status === 'success') {
        setInsights(response.data);
        // Refresh analytics to get updated survey data with insights
        await fetchAnalytics();
      }
    } catch (error) {
      console.error('Error generating insights:', error);
    } finally {
      setIsGeneratingInsights(false);
    }
  };

  const radius = 40;
  const center = 50;
  const segments = useMemo(() => {
    if (!Array.isArray(analyticsData) || analyticsData.length === 0) return [];
    
    // Special case: if there's only one answer with 100%, render a full circle
    if (analyticsData.length === 1 && analyticsData[0].percentage === 100) {
      return [{
        ...analyticsData[0],
        originalIndex: 0,
        startAngle: 0,
        endAngle: 360,
      }];
    }
    
    // Special case: if there's only one answer but not 100%, still render it as a full circle
    if (analyticsData.length === 1) {
      return [{
        ...analyticsData[0],
        originalIndex: 0,
        startAngle: 0,
        endAngle: 360,
      }];
    }
    
    let startAngle = 0;
    const calculatedSegments = analyticsData.map((response, index) => {
      const angle = (response.percentage / 100) * 360;
      const endAngle = startAngle + angle;
      const segment = {
        ...response,
        originalIndex: index,
        startAngle,
        endAngle,
      };
      startAngle = endAngle;
      return segment;
    });
    
    // Ensure we have valid segments
    if (calculatedSegments.length === 0) {
      return [];
    }
    
    // If the last segment doesn't complete the circle, adjust it
    const lastSegment = calculatedSegments[calculatedSegments.length - 1];
    if (lastSegment.endAngle < 360) {
      lastSegment.endAngle = 360;
    }
    
    // If the total exceeds 360, normalize the last segment
    if (lastSegment.endAngle > 360) {
      lastSegment.endAngle = 360;
    }
    
    return calculatedSegments;
  }, [analyticsData]);

  const renderMultipleChoice = () => {
    return (
      <>
        <div className="flex mb-8">
          <div className="w-1/3">
            <div className="relative w-full pt-[100%]">
              <div className="absolute inset-0">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  <circle cx="50" cy="50" r={radius} fill="white" />
                  {totalAnswers === 0 || segments.length === 0 ? (
                    <circle cx="50" cy="50" r="40" fill="transparent" stroke="#D1D5DB" strokeWidth="20" strokeDasharray="251.2" strokeDashoffset="0" transform="rotate(-90 50 50)" />
                  ) : (
                    segments.map((segment) => {
                      const isOther = segment.option === "Other";
                      const strokeColor = isOther ? "rgb(180, 180, 180)" : GraphColors[segment.originalIndex % GraphColors.length];
                      const path = describeArc(center, center, radius, segment.startAngle, segment.endAngle);
                      return (
                        <path
                          key={segment.option}
                          d={path}
                          fill="none"
                          stroke={strokeColor}
                          strokeWidth={20}
                          className="transition-all duration-300 hover:opacity-80 cursor-pointer"
                          onMouseMove={(e) => {
                            setTooltip({
                              visible: true,
                              content: `${segment.option}: ${segment.percentage.toFixed(1)}%`,
                              x: e.clientX,
                              y: e.clientY,
                            });
                          }}
                          onMouseLeave={() => {
                            setTooltip(null);
                          }}
                        />
                      );
                    })
                  )}
                </svg>
              </div>
            </div>
          </div>
          <div className="w-2/3 pl-6">
            <h3 className="font-medium mb-4">{surveyData?.question}</h3>
            {totalAnswers === 0 ? (
              // Show all options with 0% when there are no answers
              surveyData?.option?.map((option, idx) => {
                const colorClass = BarColors[idx % BarColors.length];
                const hoverColorClass = HoverColors[idx % HoverColors.length];
                return (
                  <div key={idx} className="mb-4 group">
                    <div className="flex justify-between mb-1">
                      <span className="group-hover:font-medium transition-all">{option}</span>
                      <span className={`group-hover:${hoverColorClass} transition-colors`}>0%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 group-hover:brightness-110 ${colorClass}`}
                        style={{ width: "0%" }}
                      ></div>
                    </div>
                  </div>
                );
              })
            ) : (
              analyticsData?.map((response, idx) => {
                const isOther = response.option === "Other";
                const colorClass = isOther ? 'bg-gray-400' : BarColors[idx % BarColors.length];
                const hoverColorClass = isOther ? 'text-gray-500' : HoverColors[idx % HoverColors.length];
                return (
                  <div key={idx} className="mb-4 group">
                    <div className="flex justify-between mb-1">
                      <span className="group-hover:font-medium transition-all">{response.option}</span>
                      <span className={`group-hover:${hoverColorClass} transition-colors`}>{response.percentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 group-hover:brightness-110 ${colorClass}`}
                        style={{ width: `${response.percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
        <div>
          <h3 className="font-medium mb-3">
            Other Responses ({otherPercentage}%)
          </h3>
          <ul className="list-disc pl-5 space-y-1 text-gray-600">
            {otherResponses?.length > 0 ? otherResponses?.map((other, idx) => (
              <li key={idx} className="hover:text-black transition-colors">
                {other}
              </li>
            )) : (
              <p className='italic text-gray-400 text-[13px] font-[500]'>No Other Response Occurs</p>
            )}
          </ul>
        </div>
      </>
    );
  };

  const renderOpenEnded = () => (
    <div className="px-4">
      <h3 className="text-[18px] font-[500] mt-8 mb-5">{surveyData?.question}</h3>
      <div className="space-y-4 text-[16px] font-[400]">
        {answersWithTimestamp?.length > 0 ? answersWithTimestamp?.map((response, idx) => {
          const dynamicColor = GraphColors[idx % GraphColors.length];
          return (
            <div key={idx} className="p-4 bg-gray-50 rounded-md shadow-sm hover:shadow-md transition-shadow">
              <div className="text-gray-800 flex leading-6">
                <span className="flex-shrink-0 mt-[9px] mr-2">
                  <div
                    style={{ backgroundColor: dynamicColor }}
                    className="w-[8px] h-[8px] rounded-full"
                  />
                </span>
                <span>{response?.answer}</span>
              </div>
              {/* Optional Timestamp */}
              <p className="text-[14px] text-[#6B7280] font-[400] mt-2 ml-4">
                {moment(response?.createdAt).format('YYYY-MM-DD HH:mm')}
              </p>
            </div>
          );
        }) : (
          <p>😞 OPPS! No <b>Answer</b> Found.</p>
        )}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto p-4 md:p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <div className="bg-white rounded-lg p-6">
                  <div className="h-6 bg-gray-200 rounded w-1/2 mb-4"></div>
                  <div className="h-64 bg-gray-200 rounded"></div>
                </div>
              </div>
              <div className="lg:col-span-1">
                <div className="bg-white rounded-lg p-6">
                  <div className="h-6 bg-gray-200 rounded w-1/2 mb-4"></div>
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-4 bg-gray-200 rounded"></div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!surveyData) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] relative">
        <div className='h-[53px] bg-[#F7F8FA] w-full'></div>
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center fixed w-full z-[999] top-0">
          <Link
            to="/dashboard"
            className="text-gray-600 hover:text-gray-900 flex items-center mr-4 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </Link>
          <h1 className="text-xl font-semibold">Survey Analytics</h1>
        </div>

        <div className="max-w-7xl mx-auto p-4 md:p-6">
          <div className="bg-white rounded-lg p-8 text-center">
            <div className="text-4xl font-bold text-[#2962FF] mb-4">📊</div>
            <h2 className="text-xl font-semibold mb-2">Analytics Not Found</h2>
            <p className="text-gray-600">
              The analytics for this survey could not be loaded.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F8FA] relative">
      <div className='h-[53px] bg-[#F7F8FA] w-full'></div>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center fixed w-full z-[999] top-0">
        <Link
          to="/dashboard"
          className="text-gray-600 hover:text-gray-900 flex items-center mr-4 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </Link>
        <h1 className="text-xl font-semibold">{surveyName} Analytics</h1>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Response Distribution */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-all duration-200">
            <h2 className="text-xl font-semibold mb-6">Response Distribution</h2>
            {isMultipleChoice ? renderMultipleChoice() : renderOpenEnded()}
          </div>
        </div>

        {/* Right Column - AI Chats & Insights */}
        <div className="lg:col-span-1 space-y-4">
          {/* AI Chats */}
          {id && <AdminAIChatList surveyId={id} answersWithTimestamp={answersWithTimestamp} isLoading={isLoading} />}
          
          {/* Metrics */}
          <div className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-all duration-200">
            <h3 className="text-lg font-semibold mb-4">Overview</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Responses</span>
                <span className="text-lg font-semibold text-gray-900">{totalAnswers}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Recent (7 days)</span>
                <span className="text-lg font-semibold text-[#2962FF]">
                  {answersWithTimestamp?.filter(answer => 
                    moment(answer.createdAt).isAfter(moment().subtract(7, 'days'))
                  ).length || 0}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Survey Status</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  surveyData?.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {surveyData?.active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          {/* AI Insights */}
          <div className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-all duration-200">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <span className="bg-blue-100 p-1 rounded-md mr-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </span>
              AI Insights
            </h2>
            <div className="space-y-6">
              {totalAnswers === 0 && (
                <div className='flex flex-col justify-center items-center my-[70px]'>
                  <div className='bg-[#646cff35] w-[52px] h-[52px] rounded-[40px] flex justify-center items-center'>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#2833ffaa]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <p className='text-[18px] font-[600] text-[#000000] mt-[25px] text-center'>Our system is waiting for customer data</p>
                  <p className='text-[14px] font-[500] text-[#4B5563] mt-[8px] text-center'>Once we have enough, we'll provide insights to help grow your store</p>
                </div>
              )}
              {totalAnswers > 0 && insights.length === 0 && (
                <EmptyState
                  icon=""
                  title=""
                  description=""
                  buttonLabel="Generate Insights"
                  onButtonClick={generateInsights}
                  className=""
                  loader={isGeneratingInsights}
                />
              )}
              {insights.length > 0 && totalAnswers > 0 && (
                <div className="transition-all duration-300">
                  <div className="space-y-4">
                    {insights.map((insight, index) => (
                      <div
                        key={index}
                        className="space-y-2 p-3 rounded-lg hover:bg-gray-50 transition-all duration-200"
                      >
                        <h3 className="font-medium flex items-center">
                          <span className="mr-2">{insight.icon}</span>
                          <span>{insight.heading}</span>
                        </h3>
                        <p className="text-gray-600 hover:text-gray-800 transition-colors text-[14px]">
                          {insight.description}
                        </p>
                      </div>
                    ))}
                  </div>
                  <EmptyState
                    icon=""
                    title=""
                    description=""
                    buttonLabel="Generate More Insights"
                    onButtonClick={generateInsights}
                    className=""
                    loader={isGeneratingInsights}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {tooltip && tooltip.visible && (
        <div
          style={{
            position: 'fixed',
            top: tooltip.y,
            left: tooltip.x,
            transform: 'translate(15px, -20px)',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
          className="bg-gray-900 text-white text-sm rounded-md px-3 py-1.5 shadow-lg z-50 transition-opacity duration-200"
        >
          {tooltip.content}
        </div>
      )}
    </div>
  );
}
